import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { logger } from './logger';
import { Log, LOG_LEVEL_TO_LOG_LEVEL_ID, LogLevel } from './logger.model';

// This logger service saves logs in memory for display in the UI, unlike standard loggers which only output to console.
// Use this when you need to show logs in the application UI.
// For example, in basket checkout, so cashiers can see which products were added.
@Injectable({
  providedIn: 'root',
})
export class LoggingService {
  // Max number of logs to be kept in memory
  LOG_SIZE_LIMIT = 200;

  // Ordering is from newest to oldest
  private logsSubject$ = new BehaviorSubject<readonly Log[]>([]);

  log(logLevelName: LogLevel, message: string) {
    const logLevelId = LOG_LEVEL_TO_LOG_LEVEL_ID[logLevelName];
    logger.log(logLevelId, logLevelName, message);

    const logsPrevious = this.logsSubject$.getValue();
    const logsNew = [
      {
        level: logLevelName,
        message,
        timestamp: new Date(),
        levelId: logLevelId,
      },
      ...logsPrevious,
    ];
    if (logsNew.length > this.LOG_SIZE_LIMIT) {
      logsNew.splice(this.LOG_SIZE_LIMIT, logsNew.length - this.LOG_SIZE_LIMIT);
    }
    this.logsSubject$.next(logsNew);
  }

  // getter for logs, order is from newest to oldest
  logs$({
    logAmount = this.LOG_SIZE_LIMIT,
    logLevelMin = 'silly',
  }: {
    logAmount?: number;
    logLevelMin?: LogLevel;
  }): Observable<readonly Log[]> {
    return this.logsSubject$
      .asObservable()
      .pipe(
        map((logs) =>
          logs
            .filter(
              (log) => log.levelId >= LOG_LEVEL_TO_LOG_LEVEL_ID[logLevelMin],
            )
            .slice(0, logAmount),
        ),
      );
  }

  silly(message: string) {
    this.log('silly', message);
  }

  trace(message: string) {
    this.log('trace', message);
  }

  debug(message: string) {
    this.log('debug', message);
  }

  info(message: string) {
    this.log('info', message);
  }

  warn(message: string) {
    this.log('warn', message);
  }

  error(message: string) {
    this.log('error', message);
  }

  fatal(message: string) {
    this.log('fatal', message);
  }
}
